// pages/Agents/AgentSpace/AgentSpaceTaskDetail.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Chip,
    Collapse,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonIcon from '@mui/icons-material/Person';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EmailIcon from '@mui/icons-material/Email';
import PollIcon from '@mui/icons-material/Poll';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChatIcon from '@mui/icons-material/Chat';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TimerIcon from '@mui/icons-material/Timer';
import { marked } from 'marked';
import axiosInstance from '../../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#8b5cf6',
    success: '#059669',
    danger: '#dc2626',
    warning: '#f59e0b',
};

const AGENT_COLORS = {
    survey: '#3b82f6',
    crm: '#f59e0b',
    chat_history: '#6366f1',
    events: '#059669',
    gmail: '#dc2626',
    outlook: '#0078d4',
    calendar: '#0891b2',
    agent: '#8b5cf6',
};

const AGENT_ICONS = {
    survey: <PollIcon sx={{ fontSize: 20 }} />,
    crm: <PeopleIcon sx={{ fontSize: 20 }} />,
    chat_history: <ChatIcon sx={{ fontSize: 20 }} />,
    events: <EventIcon sx={{ fontSize: 20 }} />,
    gmail: <MailOutlineIcon sx={{ fontSize: 20 }} />,
    outlook: <EmailIcon sx={{ fontSize: 20 }} />,
    calendar: <CalendarMonthIcon sx={{ fontSize: 20 }} />,
    agent: <AutoAwesomeIcon sx={{ fontSize: 20 }} />,
};

function formatDuration(ms) {
    if (!ms) return '0s';
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
}

function formatTimeAgo(isoString) {
    if (!isoString) return '';
    const now = new Date();
    const date = new Date(isoString);
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

function getStatusConfig(status) {
    const s = (status || '').toLowerCase();
    if (['completed', 'delivered', 'replied_ai', 'replied_manual', 'reply_generated', 'active', 'sent'].includes(s))
        return { color: '#059669', bg: 'rgba(5,150,105,0.1)', label: s === 'replied_ai' ? 'AI Replied' : s === 'reply_generated' ? 'Reply Ready' : s.charAt(0).toUpperCase() + s.slice(1) };
    if (['failed', 'error', 'cancelled'].includes(s))
        return { color: '#dc2626', bg: 'rgba(220,38,38,0.1)', label: s.charAt(0).toUpperCase() + s.slice(1) };
    if (['pending', 'processing', 'new', 'in_progress'].includes(s))
        return { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', label: s === 'in_progress' ? 'In Progress' : s.charAt(0).toUpperCase() + s.slice(1) };
    return { color: colors.textTertiary, bg: 'rgba(148,163,184,0.1)', label: s || 'Unknown' };
}

function MiniStat({ label, value, icon, color }) {
    return (
        <Box sx={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 0.75,
            py: 1, px: 1.25, borderRadius: '10px',
            bgcolor: `${color}08`, border: `1px solid ${color}12`,
        }}>
            <Box sx={{
                width: 28, height: 28, borderRadius: '8px',
                bgcolor: `${color}12`, color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                '& svg': { fontSize: 15 },
            }}>
                {icon}
            </Box>
            <Box>
                <Typography sx={{ fontFamily, fontSize: '0.95rem', fontWeight: 700, color, lineHeight: 1.2 }}>
                    {value}
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.55rem', fontWeight: 500, color: colors.textTertiary }}>
                    {label}
                </Typography>
            </Box>
        </Box>
    );
}

function DetailStepItem({ step, index, expanded, onToggle }) {
    const isCompleted = step.status === 'completed';
    const isFailed = step.status === 'failed';
    const hasOutput = step.output && (typeof step.output === 'object' ? Object.keys(step.output).length > 0 : !!step.output);

    return (
        <Box sx={{
            display: 'flex', gap: 1, py: 1,
            borderLeft: `2px solid ${isCompleted ? colors.success : isFailed ? colors.danger : colors.textTertiary}25`,
            pl: 1.5, ml: 0.5, mb: 0.25,
        }}>
            <Box sx={{ flexShrink: 0, pt: 0.25 }}>
                {isCompleted ? (
                    <CheckCircleIcon sx={{ fontSize: 16, color: colors.success }} />
                ) : isFailed ? (
                    <ErrorIcon sx={{ fontSize: 16, color: colors.danger }} />
                ) : (
                    <AccessTimeIcon sx={{ fontSize: 16, color: colors.textTertiary }} />
                )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography sx={{
                        fontFamily, fontSize: '0.75rem', fontWeight: 500,
                        color: isFailed ? colors.danger : colors.textPrimary,
                    }}>
                        <Typography component="span" sx={{ fontFamily, fontSize: '0.65rem', color: colors.textTertiary, mr: 0.5 }}>
                            [{index + 1}]
                        </Typography>
                        {step.label || step.action_type}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                        {step.duration_ms && (
                            <Typography sx={{ fontFamily, fontSize: '0.6rem', color: colors.textTertiary }}>
                                {step.duration_ms}ms
                            </Typography>
                        )}
                        {hasOutput && (
                            <IconButton size="small" onClick={onToggle} sx={{ p: 0.25 }}>
                                {expanded ? <ExpandLessIcon sx={{ fontSize: 14, color: colors.textTertiary }} /> : <ExpandMoreIcon sx={{ fontSize: 14, color: colors.textTertiary }} />}
                            </IconButton>
                        )}
                    </Box>
                </Box>
                {isFailed && step.output?.error && (
                    <Typography sx={{ fontFamily, fontSize: '0.65rem', color: colors.danger, mt: 0.25 }}>
                        {typeof step.output.error === 'string' ? step.output.error : JSON.stringify(step.output.error)}
                    </Typography>
                )}
                <Collapse in={expanded}>
                    {hasOutput && (
                        <Box sx={{
                            mt: 0.75, p: 1, bgcolor: 'rgba(0,0,0,0.03)',
                            borderRadius: '8px', maxHeight: 250, overflow: 'auto',
                            '&::-webkit-scrollbar': { width: '4px' },
                            '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: '4px' },
                        }}>
                            <Typography component="pre" sx={{
                                fontFamily: "'Fira Code', monospace",
                                fontSize: '0.6rem', color: colors.textSecondary,
                                whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0,
                            }}>
                                {typeof step.output === 'string' ? step.output : JSON.stringify(step.output, null, 2)}
                            </Typography>
                        </Box>
                    )}
                </Collapse>
            </Box>
        </Box>
    );
}

function DetailMessageItem({ msg }) {
    const isUser = msg.role === 'USER';
    const createMarkup = (text) => {
        try { return { __html: marked.parse(text || '') }; }
        catch { return { __html: text || '' }; }
    };

    return (
        <Box sx={{
            display: 'flex', gap: 1, mb: 1.5,
            flexDirection: isUser ? 'row-reverse' : 'row',
        }}>
            <Box sx={{
                width: 26, height: 26, borderRadius: '8px', flexShrink: 0,
                bgcolor: isUser ? '#e2e8f0' : 'rgba(139,92,246,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                {isUser
                    ? <PersonIcon sx={{ fontSize: 14, color: colors.textSecondary }} />
                    : <AutoAwesomeIcon sx={{ fontSize: 14, color: colors.accent }} />
                }
            </Box>
            <Box sx={{
                maxWidth: '85%', px: 2, py: 1,
                borderRadius: isUser ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                bgcolor: isUser ? 'rgba(139,92,246,0.08)' : 'rgba(255,255,255,0.9)',
                border: '1px solid rgba(0,0,0,0.06)',
            }}>
                {isUser ? (
                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textPrimary, lineHeight: 1.6 }}>
                        {msg.message}
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            fontFamily, fontSize: '0.8rem', color: colors.textPrimary, lineHeight: 1.6,
                            '& p': { margin: '0.3em 0' },
                            '& p:first-of-type': { marginTop: 0 },
                            '& p:last-of-type': { marginBottom: 0 },
                            '& ul, & ol': { pl: 2.5, my: 0.5 },
                            '& li': { mb: 0.25 },
                            '& code': {
                                bgcolor: 'rgba(0,0,0,0.04)', px: 0.75, py: 0.25,
                                borderRadius: '4px', fontSize: '0.75rem',
                                fontFamily: "'Fira Code', monospace",
                            },
                            '& strong': { fontWeight: 700 },
                            '& a': { color: colors.accent, textDecoration: 'none' },
                        }}
                        dangerouslySetInnerHTML={createMarkup(msg.message)}
                    />
                )}
            </Box>
        </Box>
    );
}

export default function AgentSpaceTaskDetail({ activity, chatbotId, onBack }) {
    const [loading, setLoading] = useState(true);
    const [conversation, setConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [expandedSteps, setExpandedSteps] = useState({});

    useEffect(() => {
        const fetchDetail = async () => {
            if (!activity?.metadata?.session_code) {
                setLoading(false);
                return;
            }
            try {
                const resp = await axiosInstance.get(
                    `/api/agent-space/conversations/${activity.metadata.session_code}/messages`
                );
                if (resp.data.success) {
                    setConversation(resp.data.data.conversation);
                    setMessages(resp.data.data.messages);
                }
            } catch (error) {
                console.error('Failed to load task detail:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchDetail();
    }, [activity]);

    const agentColor = AGENT_COLORS[activity.agent_type] || colors.accent;
    const agentIcon = AGENT_ICONS[activity.agent_type] || AGENT_ICONS.agent;
    const statusConfig = getStatusConfig(activity.status);

    const allSteps = messages
        .filter(m => m.role === 'AGENT' && m.steps?.length > 0)
        .flatMap(m => m.steps);
    const completedSteps = allSteps.filter(s => s.status === 'completed').length;
    const failedSteps = allSteps.filter(s => s.status === 'failed').length;
    const totalDuration = allSteps.reduce((sum, s) => sum + (s.duration_ms || 0), 0);

    const selectedResources = conversation?.selected_resources || activity.metadata?.selected_resources || [];

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Back Button */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 2.5, pt: 2, pb: 1,
                borderBottom: '1px solid rgba(0,0,0,0.04)',
            }}>
                <IconButton
                    onClick={onBack}
                    size="small"
                    sx={{
                        color: colors.textTertiary,
                        '&:hover': { bgcolor: 'rgba(139,92,246,0.1)', color: colors.accent },
                    }}
                >
                    <ArrowBackIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary }}>
                    Back to Activities
                </Typography>
            </Box>

            {/* Scrollable Content */}
            <Box sx={{
                flex: 1, overflow: 'auto', px: 2.5, py: 2,
                '&::-webkit-scrollbar': { width: '4px' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.1)', borderRadius: '4px' },
            }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress size={28} sx={{ color: colors.accent }} />
                    </Box>
                ) : (
                    <>
                        {/* Task Header */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                            <Box sx={{
                                width: 40, height: 40, minWidth: 40, borderRadius: '12px',
                                bgcolor: `${agentColor}12`, color: agentColor,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                {agentIcon}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{
                                    fontFamily, fontSize: '1rem', fontWeight: 700,
                                    color: colors.textPrimary, lineHeight: 1.3,
                                }}>
                                    {conversation?.title || activity.resource_name}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
                                    <Chip
                                        size="small"
                                        label={activity.agent_type === 'chat_history' ? 'Chat History' : activity.agent_type}
                                        sx={{
                                            fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 22,
                                            bgcolor: `${agentColor}12`, color: agentColor,
                                            textTransform: 'capitalize',
                                        }}
                                    />
                                    <Chip
                                        size="small"
                                        icon={statusConfig.color === '#059669' ? <CheckCircleIcon sx={{ fontSize: '12px !important' }} /> :
                                            statusConfig.color === '#dc2626' ? <ErrorIcon sx={{ fontSize: '12px !important' }} /> :
                                                <AccessTimeIcon sx={{ fontSize: '12px !important' }} />}
                                        label={statusConfig.label}
                                        sx={{
                                            fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 22,
                                            bgcolor: statusConfig.bg, color: statusConfig.color,
                                            '& .MuiChip-icon': { color: statusConfig.color },
                                        }}
                                    />
                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: colors.textTertiary }}>
                                        {formatTimeAgo(activity.timestamp)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Mini Stats */}
                        {allSteps.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <MiniStat label="Total Steps" value={allSteps.length} icon={<AssignmentIcon />} color={colors.accent} />
                                <MiniStat label="Completed" value={completedSteps} icon={<CheckCircleIcon />} color={colors.success} />
                                <MiniStat label="Failed" value={failedSteps} icon={<ErrorIcon />} color={colors.danger} />
                                <MiniStat label="Duration" value={formatDuration(totalDuration)} icon={<TimerIcon />} color={colors.warning} />
                            </Box>
                        )}

                        {/* Selected Resources */}
                        {selectedResources.length > 0 && (
                            <Box sx={{
                                mb: 2, p: 1.5, borderRadius: '10px',
                                bgcolor: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.04)',
                            }}>
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: colors.textTertiary, mb: 0.75 }}>
                                    Resources Used
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selectedResources.map((r, i) => (
                                        <Chip
                                            key={i}
                                            size="small"
                                            label={r.label || r.name || `Resource ${i + 1}`}
                                            sx={{
                                                fontFamily, fontSize: '0.65rem', fontWeight: 500, height: 22,
                                                bgcolor: `${agentColor}08`, color: colors.textSecondary,
                                                border: `1px solid ${agentColor}15`,
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Execution Steps */}
                        {allSteps.length > 0 && (
                            <Box sx={{
                                mb: 2, p: 1.5, borderRadius: '12px',
                                bgcolor: 'rgba(139,92,246,0.03)',
                                border: '1px solid rgba(139,92,246,0.08)',
                            }}>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 600, color: colors.textPrimary, mb: 1 }}>
                                    Execution Steps
                                </Typography>
                                {allSteps.map((step, idx) => (
                                    <DetailStepItem
                                        key={idx}
                                        step={step}
                                        index={idx}
                                        expanded={!!expandedSteps[idx]}
                                        onToggle={() => setExpandedSteps(prev => ({ ...prev, [idx]: !prev[idx] }))}
                                    />
                                ))}
                            </Box>
                        )}

                        {/* Conversation Messages */}
                        {messages.length > 0 && (
                            <Box>
                                <Typography sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 600, color: colors.textPrimary, mb: 1.5 }}>
                                    Conversation
                                </Typography>
                                {messages.map((msg) => (
                                    <DetailMessageItem key={msg.id} msg={msg} />
                                ))}
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
